import React, { useMemo, useRef, useState } from "react";
import PitchPlotAudio from "./PitchPlotAudio";
import IntensityPlotAudio from "./IntensityPlotAudio";
import PitchIntensityAnimationUser from "./PitchIntensityAnimationUser";
import MusicNotationView from "./MusicNotationView";
import ColorCodedWordsView from "./ColorCodedWordsView";
import { defaultColormap } from "./colormaps";

const IntonationFeedbackDisplay = ({
  assessment,
  segment,
  view,
  isAdmin,
  audioUrl,
  narratorAudioUrl,
}) => {
  const [selectedColormap, setSelectedColormap] = useState(
    defaultColormap || "speechStable"
  );
  const [currentTime, setCurrentTime] = useState(0);
  const [showLegend, setShowLegend] = useState(true);

  const [isPlaying, setIsPlaying] = useState(false);
  const [playerSpeed, setPlayerSpeed] = useState(1);
  const [duration, setDuration] = useState(0);

  const recordedAudioRef = useRef(null);

  const audio =
    audioUrl ||
    (assessment?.intonation?.file
      ? `${process.env.REACT_APP_USER_API_URL}/${assessment.intonation.file}`
      : null);

  const togglePlay = async () => {
    const el = recordedAudioRef.current;
    if (!el || !audio) return;

    try {
      if (el.paused) {
        await el.play();
      } else {
        el.pause();
      }
    } catch (err) {
      console.error("Audio play/pause failed:", err);
    }
  };

  const setSpeed = (speed) => {
    setPlayerSpeed(speed);
    if (recordedAudioRef.current) {
      recordedAudioRef.current.playbackRate = speed;
    }
  };

  const fmt = (t) => {
    if (!Number.isFinite(t)) return "0:00";
    const m = Math.floor(t / 60);
    const s = Math.floor(t % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  };

  const progressPct = useMemo(() => {
    if (!duration || !Number.isFinite(duration)) return 0;
    return Math.max(0, Math.min(100, (currentTime / duration) * 100));
  }, [currentTime, duration]);

  return (
    <div className="flex flex-col w-full">
      {assessment?.intonation?.intensity && assessment?.time_stamped ? (
        <div className="flex flex-col gap-3 w-full">
          {/* Single shared audio element */}
          <audio
            ref={recordedAudioRef}
            src={audio || undefined}
            preload="metadata"
            className="hidden"
            onLoadedMetadata={(e) => {
              const d = e.currentTarget.duration;
              setDuration(Number.isFinite(d) ? d : 0);
              e.currentTarget.playbackRate = playerSpeed;
            }}
            onTimeUpdate={(e) => {
              setCurrentTime(e.currentTarget.currentTime);
            }}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
          />

          {view === "pitch" && (
            <PitchPlotAudio
              audioRef={recordedAudioRef}
              intensity={assessment.intonation.intensity}
              time_stamped={assessment.time_stamped}
              speed={playerSpeed}
            />
          )}

          {view === "intensity" && (
            <IntensityPlotAudio
  audioRef={recordedAudioRef}
  intensity={assessment.intonation.intensity}
  speed={playerSpeed}
/>
          )}

          {view === "combined" && (
            <PitchIntensityAnimationUser
  audioRef={recordedAudioRef}
  intensity={assessment.intonation.intensity.intensity}
  pitch={assessment.intonation.intensity.pitch}
  speed={playerSpeed}
  selectedColormap={selectedColormap}
  onColormapChange={setSelectedColormap}
/>
          )}

          {view === "music" && (
            <MusicNotationView
              pitch={assessment.intonation.intensity.pitch}
              intensity={assessment.intonation.intensity.intensity}
              pitch1stDerivative={
                assessment.intonation.intensity.pitch_1st_derivative
              }
              segment={assessment.time_stamped}
              broadcastTime={currentTime}
            />
          )}

          {view === "colors" && assessment.time_stamped?.words && (
            <ColorCodedWordsView
              words={assessment.time_stamped.words}
              pitch={assessment.intonation.intensity.pitch}
              intensity={assessment.intonation.intensity.intensity}
              segment={assessment.time_stamped}
              broadcastTime={currentTime}
              showLegend={showLegend}
              onToggleLegend={() => setShowLegend(!showLegend)}
            />
          )}

          {/* Paler integrated controls */}
          {audio && (
            <div className="rounded-2xl border border-emerald-100/80 bg-emerald-50/45 backdrop-blur-sm px-4 py-4">
              
              <div className="">
                <div className="h-1.5 rounded-full bg-white/80 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-green-500"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>

              <div className="mt-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between">
                <button
                  type="button"
                  onClick={togglePlay}
                  disabled={!audio}
                  className={[
                    "inline-flex items-center justify-center gap-2 rounded-xl font-semibold transition-all border shadow-sm",
                    audio
                      ? "bg-white text-slate-800 border-emerald-200 hover:bg-emerald-50"
                      : "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed",
                    "h-11 px-4",
                  ].join(" ")}
                >
                  <span className="text-lg leading-none">
                    {isPlaying ? "⏸" : "▶️"}
                  </span>
                  <span className="text-sm">
                    {isPlaying ? "Pause" : "Play"} my recording
                  </span>
                </button>

                <div className="flex items-center gap-3 justify-between sm:justify-end">
                  <span className="text-xs font-medium text-slate-500 whitespace-nowrap">
                    Playback speed
                  </span>

                  <div className="inline-flex rounded-xl bg-white/90 border border-emerald-100 shadow-sm p-1">
                    {[0.25, 0.5, 1].map((speed) => {
                      const active = playerSpeed === speed;
                      return (
                        <button
                          key={speed}
                          type="button"
                          onClick={() => setSpeed(speed)}
                          className={[
                            "px-3 py-1.5 rounded-lg text-sm font-semibold transition-all",
                            active
                              ? "bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow"
                              : "text-slate-600 hover:bg-emerald-50",
                          ].join(" ")}
                        >
                          {speed}x
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="mt-2 text-[11px] text-slate-500">
                Tip: listen for the <span className="font-semibold">rise/fall</span>{" "}
                on each word and compare the moving guide above.
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex justify-center items-center p-6 bg-gray-50 rounded-xl border border-gray-200">
          <p className="text-gray-500 text-sm">
            Loading user intonation analysis...
          </p>
        </div>
      )}
    </div>
  );
};

export default IntonationFeedbackDisplay;